import { useEffect, useState } from "react";
import { api, formatNaira, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip } from "@/components/Chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Plus, TrendingUp, TrendingDown, Coins, Download } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { downloadCsv } from "@/lib/exports";

export default function Accounting() {
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("income");
  const [form, setForm] = useState({ business_unit: "FigureWeb", amount: 0, description: "", date: new Date().toISOString().slice(0, 10) });

  const load = () => {
    api.get("/incomes").then(r => setIncomes(r.data));
    api.get("/expenses").then(r => setExpenses(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.description) return toast.error("Description required");
    const endpoint = type === "income" ? "/incomes" : "/expenses";
    await api.post(endpoint, { ...form, amount: Number(form.amount) });
    toast.success("Recorded");
    setOpen(false);
    setForm({ business_unit: "FigureWeb", amount: 0, description: "", date: new Date().toISOString().slice(0, 10) });
    load();
  };

  const totalIncome = incomes.reduce((s, x) => s + Number(x.amount || 0), 0);
  const totalExpense = expenses.reduce((s, x) => s + Number(x.amount || 0), 0);
  const profit = totalIncome - totalExpense;

  const byUnit = (list) => {
    const m = { FigureWeb: 0, FigureCX: 0, Telecom: 0, Executive: 0 };
    list.forEach(x => { m[x.business_unit] = (m[x.business_unit] || 0) + Number(x.amount || 0); });
    return m;
  };
  const incByU = byUnit(incomes);
  const expByU = byUnit(expenses);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Accounting"
        subtitle="Income and expense tracking by business unit"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => downloadCsv("incomes")} data-testid="export-incomes-btn">
              <Download className="w-4 h-4 mr-1" /> Income CSV
            </Button>
            <Button variant="outline" onClick={() => downloadCsv("expenses")} data-testid="export-expenses-btn">
              <Download className="w-4 h-4 mr-1" /> Expenses CSV
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="add-entry-btn">
                  <Plus className="w-4 h-4 mr-1" /> Record Entry
                </Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New {type}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Type</Label>
                    <Select value={type} onValueChange={setType}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="income">Income</SelectItem>
                        <SelectItem value="expense">Expense</SelectItem>
                      </SelectContent>
                    </Select></div>
                  <div><Label>Business Unit</Label>
                    <Select value={form.business_unit} onValueChange={v => setForm({...form, business_unit: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FigureWeb">FigureWeb</SelectItem>
                        <SelectItem value="FigureCX">FigureCX</SelectItem>
                        <SelectItem value="Telecom">Telecom</SelectItem>
                        <SelectItem value="FigureAcademy">Figure Academy</SelectItem>
                        <SelectItem value="Executive">Executive</SelectItem>
                      </SelectContent>
                    </Select></div>
                </div>
                <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="entry-desc-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Amount (₦)</Label><Input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} /></div>
                  <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} /></div>
                </div>
              </div>
              <DialogFooter><Button onClick={save} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="save-entry-btn">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="kpi-card">
          <div className="flex justify-between"><div className="text-xs uppercase tracking-wider text-slate-500">Revenue</div><TrendingUp className="w-4 h-4 text-emerald-600" /></div>
          <div className="font-heading font-bold text-3xl text-emerald-600 num">{formatNaira(totalIncome)}</div>
        </div>
        <div className="kpi-card">
          <div className="flex justify-between"><div className="text-xs uppercase tracking-wider text-slate-500">Expenses</div><TrendingDown className="w-4 h-4 text-red-500" /></div>
          <div className="font-heading font-bold text-3xl text-red-500 num">{formatNaira(totalExpense)}</div>
        </div>
        <div className="kpi-card">
          <div className="flex justify-between"><div className="text-xs uppercase tracking-wider text-slate-500">Net Profit</div><Coins className="w-4 h-4 text-brand-aqua" /></div>
          <div className={`font-heading font-bold text-3xl num ${profit >= 0 ? "text-slate-900" : "text-red-500"}`}>{formatNaira(profit)}</div>
        </div>
      </div>

      <div className="kpi-card">
        <div className="font-heading font-semibold text-lg text-slate-900 mb-3">P&L by Business Unit</div>
        <table className="w-full text-sm">
          <thead className="table-head">
            <tr>
              <th className="text-left px-3 py-2">Unit</th>
              <th className="text-right px-3 py-2">Income</th>
              <th className="text-right px-3 py-2">Expenses</th>
              <th className="text-right px-3 py-2">Net</th>
            </tr>
          </thead>
          <tbody>
            {["FigureWeb", "FigureCX", "Telecom", "Executive"].map(u => {
              const net = incByU[u] - expByU[u];
              return (
                <tr key={u} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium text-slate-800">{u}</td>
                  <td className="px-3 py-2 text-right text-emerald-600 num">{formatNaira(incByU[u])}</td>
                  <td className="px-3 py-2 text-right text-red-500 num">{formatNaira(expByU[u])}</td>
                  <td className={`px-3 py-2 text-right font-heading font-semibold num ${net >= 0 ? "text-slate-900" : "text-red-500"}`}>{formatNaira(net)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Tabs defaultValue="incomes">
        <TabsList>
          <TabsTrigger value="incomes">Income entries</TabsTrigger>
          <TabsTrigger value="expenses">Expense entries</TabsTrigger>
        </TabsList>
        {[
          { key: "incomes", list: incomes, tone: "text-emerald-600" },
          { key: "expenses", list: expenses, tone: "text-red-500" },
        ].map(({ key, list, tone }) => (
          <TabsContent key={key} value={key} className="mt-4">
            <div className="table-wrap">
              <table className="w-full text-sm">
                <thead className="table-head">
                  <tr>
                    <th className="text-left px-4 py-3">Description</th>
                    <th className="text-left px-4 py-3">Unit</th>
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-right px-4 py-3">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map(x => (
                    <tr key={x.id} className="row">
                      <td className="px-4 py-3 text-slate-800">{x.description}</td>
                      <td className="px-4 py-3"><Chip>{x.business_unit}</Chip></td>
                      <td className="px-4 py-3 text-xs text-slate-500">{formatDate(x.date)}</td>
                      <td className={`px-4 py-3 text-right font-heading font-semibold num ${tone}`}>{formatNaira(x.amount)}</td>
                    </tr>
                  ))}
                  {list.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-slate-500 text-sm">No entries.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
